/**
 * Sanitizes coordinate values by replacing commas with dots
 * @param {string|number} value - The coordinate value to sanitize
 * @returns {string} - The sanitized coordinate value
 */
export const sanitizeCoordinate = (value) => {
	if (value === null || value === undefined || value === '') return '';
	return String(value).replace(/,/g, '.');
};

/**
 * Validates if a coordinate value is valid
 * @param {string|number} value - The coordinate value
 * @param {number} min - Minimum valid value
 * @param {number} max - Maximum valid value
 * @returns {boolean} - Whether the coordinate is valid
 */
export const isValidCoordinate = (value, min, max) => {
	if (value === null || value === undefined || value === '') return true; // Empty values are considered valid (optional field)
	const num = parseFloat(sanitizeCoordinate(value));
	return !isNaN(num) && num >= min && num <= max;
};

/**
 * Sanitizes and validates a coordinates object
 * @param {Object} coordinates - Object with lat and lng properties
 * @returns {Object} - Object with sanitized coordinates and validation result
 */
export const sanitizeAndValidateCoordinates = (coordinates) => {
	const result = {
		isValid: true,
		error: null,
		sanitized: { lat: '', lng: '' }
	};

	if (!coordinates) {
		return result;
	}

	// Sanitize
	if (coordinates.lat) {
		result.sanitized.lat = sanitizeCoordinate(coordinates.lat);
	}
	if (coordinates.lng) {
		result.sanitized.lng = sanitizeCoordinate(coordinates.lng);
	}

	// Validate latitude
	if (coordinates.lat && !isValidCoordinate(coordinates.lat, -90, 90)) {
		result.isValid = false;
		result.error = 'Latitude inválida. Deve estar entre -90 e 90.';
		return result;
	}

	// Validate longitude
	if (coordinates.lng && !isValidCoordinate(coordinates.lng, -180, 180)) {
		result.isValid = false;
		result.error = 'Longitude inválida. Deve estar entre -180 e 180.';
		return result;
	}

	return result;
};

/**
 * Sanitizes coordinates for safe use with mapping libraries
 * Replaces commas with dots and converts to numbers, returns null for invalid coords
 * @param {Object} coordinates - Object with lat and lng properties
 * @returns {Object|null} - Sanitized coordinates object or null if invalid
 */
export const sanitizeCoordinatesForMap = (coordinates) => {
	if (!coordinates || !coordinates.lat || !coordinates.lng) {
		return null;
	}

	const lat = parseFloat(sanitizeCoordinate(coordinates.lat));
	const lng = parseFloat(sanitizeCoordinate(coordinates.lng));

	if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
		return null;
	}

	return { lat, lng };
};

/**
 * Tries to parse a coordinate string that might be in DMS format (e.g. 22°48'38.1"S 48°40'44.4"W).
 * Supports any order of latitude/longitude as long as cardinal directions are present.
 * @param {string} coordString - The string containing latitude and longitude.
 * @returns {Object|null} - Object { lat, lng } or null if not DMS or invalid.
 */
export const parseDMSCoordinates = (coordString) => {
    if (!coordString) return null;

    // Pattern for a single DMS coordinate with optional direction
    // Captures: 1: degrees, 2: minutes, 3: seconds, 4: direction (optional)
    // Supports seconds with dot or comma as decimal separator
    const dmsRegex = /(-?\d{1,3})[°\s]+(\d{1,2})['\s]+(\d{1,2}(?:[.,]\d+)?)["\s]*([NSEW])?/gi;

    const matches = [...coordString.matchAll(dmsRegex)];

    if (matches.length !== 2) return null;

    let lat = null;
    let lng = null;
    let isLatNext = true;

    for (const match of matches) {
        const degrees = parseFloat(match[1]);
        const minutes = parseFloat(match[2]);
        const seconds = parseFloat(match[3].replace(',', '.'));
        const direction = match[4] ? match[4].toUpperCase() : null;

        let decimal = Math.abs(degrees) + minutes / 60 + seconds / 3600;

        if (direction === 'S' || direction === 'W') {
            decimal *= -1;
        } else if (!direction && degrees < 0) {
            decimal *= -1;
        }

        if (direction) {
            if (direction === 'N' || direction === 'S') {
                lat = decimal;
            } else if (direction === 'E' || direction === 'W') {
                lng = decimal;
            }
        } else {
            if (isLatNext) {
                lat = decimal;
                isLatNext = false;
            } else {
                lng = decimal;
            }
        }
    }

    if (lat !== null && lng !== null) {
        return { lat, lng };
    }

    return null;
};

/**
 * Tries to parse a decimal coordinate string (e.g., "-22.123, -48.123").
 * @param {string} input - The string to parse.
 * @returns {Object|null} - Object { lat, lng } or null if invalid.
 */
export const parseDecimalCoordinates = (input) => {
    if (!input) return null;

    // Regex for "lat, lng" or "lat lng"
    // Supports decimal comma or dot. Relaxed precision requirement.
    // Matches: -22.123, -48.123 OR -22.123 -48.123
    const decimalRegex = /(-?\d{1,3}[.,]\d+)\s*[,\s]+\s*(-?\d{1,3}[.,]\d+)/;
    const match = input.match(decimalRegex);

    if (match) {
        const lat = parseFloat(match[1].replace(',', '.'));
        const lng = parseFloat(match[2].replace(',', '.'));
        if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            return { lat, lng };
        }
    }
    return null;
};

/**
 * Determines the type of input and parses it.
 * @param {string} input - The raw input string.
 * @returns {Object} - { type: 'COORDINATE' | 'ADDRESS', value: { lat, lng } | string }
 */
export const parseInput = (input) => {
    if (!input || typeof input !== 'string') return { type: 'INVALID', value: null };

    const trimmed = input.trim();

    // 1. Try Decimal
    const decimal = parseDecimalCoordinates(trimmed);
    if (decimal) return { type: 'COORDINATE', value: decimal };

    // 2. Try DMS
    const dms = parseDMSCoordinates(trimmed);
    if (dms) return { type: 'COORDINATE', value: dms };

    // 3. Fallback to Address
    // If it has at least 3 characters, treat as address
    if (trimmed.length > 3) {
        return { type: 'ADDRESS', value: trimmed };
    }

    return { type: 'INVALID', value: null };
};
